import React, { useState } from 'react';
import { Card } from '../common/Card';
import { Text, Typography } from '../common/Typography';
import { StatusBadge } from '../common';
import { formatDistanceToNow } from 'date-fns';

/**
 * CollapsibleNotificationItem Component
 * Base component for all notifications with collapsible details section
 */
const CollapsibleNotificationItem = ({
  notification,
  borderColor = 'blue',
  badgeVariant = 'info',
  children, // Action buttons or custom content
  detailsContent, // Content to show in collapsible section
  metadata, // Additional metadata to display (e.g., club name, timestamp)
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isUnread = !notification.is_read;
  const timeAgo = formatDistanceToNow(new Date(notification.created_at), { addSuffix: true });

  const borderColorClasses = {
    blue: 'border-l-blue-500',
    amber: 'border-l-amber-500',
    orange: 'border-l-orange-500',
    green: 'border-l-green-500',
    red: 'border-l-red-500'
  };

  return (
    <Card 
      variant={isUnread ? "default" : "muted"} 
      size="small"
      className={`${isUnread ? `border-l-4 ${borderColorClasses[borderColor]}` : ''} ${className}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Header */}
          <div className="flex items-center gap-2 mb-2">
            <Typography variant="strong" className={isUnread ? 'text-text-primary' : 'text-text-secondary'}>
              {notification.title}
            </Typography>
            {isUnread && (
              <StatusBadge variant={badgeVariant} size="small">New</StatusBadge>
            )}
          </div>
          
          {/* Message with expand/collapse button */}
          <div className="mb-3">
            <div className="flex items-start justify-between gap-2">
              <Text className={`text-sm flex-1 ${isUnread ? 'text-text-secondary' : 'text-text-muted'}`}>
                {notification.message}
              </Text>
              {(detailsContent || metadata) && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="flex-shrink-0 text-text-muted hover:text-text-secondary transition-colors"
                  aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                >
                  <svg
                    className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
            </div>

            {/* Collapsible Details Section */}
            {isExpanded && (detailsContent || metadata) && (
              <div className="bg-surface-subtle border border-border-medium p-3 rounded-lg mt-3 space-y-3">
                {detailsContent}
                {metadata && (
                  <div className="pt-2 border-t border-border-medium">
                    {metadata}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer - Timestamp */}
          <div className="flex items-center gap-4 text-xs text-text-muted mb-3">
            <span>{timeAgo}</span>
            {(detailsContent || metadata) && !isExpanded && (
              <span className="text-text-muted">• Click to expand for details</span>
            )}
          </div>

          {/* Action Buttons / Custom Content */}
          {children}
        </div>
      </div>
    </Card>
  );
};

export default CollapsibleNotificationItem;

